import React from 'react';
import { Plus, Pencil, Trash2, X, Check, AlertTriangle, Headphones } from 'lucide-react';

import { callCentersApi } from '../lib/callCentersApi';
import { usersApi } from '../lib/usersApi';
import { toast, toastSuccess, toastError } from '../lib/toast';
import { Switch } from '../components/ui/switch';
import EmptyState from '../components/common/EmptyState';
import { TableRowSkeleton } from '../components/common/TableSkeleton';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogAction,
    AlertDialogCancel,
} from '../components/ui/alert-dialog';

/**
 * /call-centers — super_admin only screen (route already guarded by AppLayout).
 *
 * Table row layout: NOM / NOM COMMERCIAL (inline pencil edit) / STATUT (pill +
 * switch) / POSITION / ACTIONS (edit + delete).
 *
 * Toggle deactivation flow:
 *   1. PATCH is_active=false (no force).
 *   2. Backend returns 409 with {code: 'confirmation_required', unresolved_count}
 *      → we open the AlertDialog and, on confirm, resend PATCH with force=true.
 *   3. If backend returned 200, deactivation was immediate (no unresolved).
 *
 * Deletion flow: DELETE returns 409 with a French sentence when blocked
 * (orders assigned OR agents attached); we surface it in a toast + keep the
 * dialog open so the admin can dismiss.
 */
function suggestNextName(existing) {
    // "CC01", "CC02", … suggest CC(max+1). If existing don't match the pattern
    // (custom names), fall back to CC01.
    const nums = existing
        .map((cc) => /^CC(\d{2,})$/.exec(cc.name))
        .filter(Boolean)
        .map((m) => parseInt(m[1], 10));
    const next = (nums.length ? Math.max(...nums) : 0) + 1;
    return `CC${String(next).padStart(2, '0')}`;
}

export default function CallCentersPage() {
    const [items, setItems] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    // Phase C.1 — liste des utilisateurs actifs pour le dropdown « Couvert par ».
    const [activeUsers, setActiveUsers] = React.useState([]);
    const [savingCoverageId, setSavingCoverageId] = React.useState(null);

    const [editingId, setEditingId] = React.useState(null);
    const [editingValue, setEditingValue] = React.useState('');

    const [createOpen, setCreateOpen] = React.useState(false);
    const [createName, setCreateName] = React.useState('');
    const [createCommercial, setCreateCommercial] = React.useState('');
    const [creating, setCreating] = React.useState(false);
    const [createError, setCreateError] = React.useState(null);

    const [confirmDeactivate, setConfirmDeactivate] = React.useState(null); // {cc, unresolved_count}
    const [confirmDelete, setConfirmDelete] = React.useState(null); // {cc}
    const [busy, setBusy] = React.useState(false);

    const load = React.useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await callCentersApi.list();
            setItems(res.call_centers || []);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => { load(); }, [load]);

    // Phase C.1 — charge la liste des utilisateurs actifs pour les dropdowns
    // « Couvert par ». Erreur non bloquante : si l'appel échoue, le dropdown
    // affichera seulement « Aucun » (pas de couverture éditable).
    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await usersApi.list();
                if (cancelled) return;
                const arr = (res?.users || res || []).filter((u) => u.is_active);
                setActiveUsers(arr);
            } catch (_e) { /* silencieux */ }
        })();
        return () => { cancelled = true; };
    }, []);

    const onCoverageChange = async (cc, raw) => {
        const next = raw === '' ? null : raw;
        if ((cc.coverage_user_id || null) === next) return;
        setSavingCoverageId(cc.id);
        try {
            const res = await callCentersApi.setCoverage(cc.id, next);
            setItems((prev) => prev.map((c) => c.id === cc.id ? res.call_center : c));
            toastSuccess(next ? `Couverture mise à jour sur ${cc.name}.` : `Couverture retirée de ${cc.name}.`);
        } catch (e) {
            toastError(e?.detail || e?.message || 'Impossible de mettre à jour la couverture');
        } finally {
            setSavingCoverageId(null);
        }
    };

    const openCreate = () => {
        setCreateName(suggestNextName(items));
        setCreateCommercial('');
        setCreateError(null);
        setCreateOpen(true);
    };

    const submitCreate = async () => {
        const name = createName.trim();
        if (!name) { setCreateError('Nom requis'); return; }
        setCreating(true);
        setCreateError(null);
        try {
            await callCentersApi.create({
                name,
                commercial_name: createCommercial.trim() || null,
            });
            toastSuccess(`Call center ${name} créé.`);
            setCreateOpen(false);
            await load();
        } catch (e) {
            setCreateError(e.message);
        } finally {
            setCreating(false);
        }
    };

    const startEditCommercial = (cc) => {
        setEditingId(cc.id);
        setEditingValue(cc.commercial_name || '');
    };
    const cancelEditCommercial = () => {
        setEditingId(null);
        setEditingValue('');
    };
    const saveEditCommercial = async (cc) => {
        try {
            await callCentersApi.update(cc.id, { commercial_name: editingValue.trim() || null });
            toastSuccess('Nom commercial mis à jour.');
            cancelEditCommercial();
            await load();
        } catch (e) {
            toastError(e.message);
        }
    };

    const onToggle = async (cc, nextActive) => {
        // Activation → immediate. Deactivation → check unresolved first.
        try {
            if (!nextActive) {
                await callCentersApi.update(cc.id, { is_active: false });
                toastSuccess(`${cc.name} désactivé.`);
                await load();
            } else {
                await callCentersApi.update(cc.id, { is_active: true });
                toastSuccess(`${cc.name} activé.`);
                await load();
            }
        } catch (e) {
            if (e.status === 409 && e.detail && typeof e.detail === 'object' && e.detail.code === 'confirmation_required') {
                // Open the destructive confirmation dialog.
                setConfirmDeactivate({ cc, unresolved_count: e.detail.unresolved_count });
                return;
            }
            toastError(e.message);
        }
    };

    const confirmDeactivateNow = async () => {
        if (!confirmDeactivate) return;
        setBusy(true);
        try {
            await callCentersApi.update(confirmDeactivate.cc.id, { is_active: false, force: true });
            toastSuccess(`${confirmDeactivate.cc.name} désactivé.`);
            setConfirmDeactivate(null);
            await load();
        } catch (e) {
            toastError(e.message);
        } finally {
            setBusy(false);
        }
    };

    const confirmDeleteNow = async () => {
        if (!confirmDelete) return;
        setBusy(true);
        try {
            await callCentersApi.remove(confirmDelete.cc.id);
            toastSuccess(`${confirmDelete.cc.name} supprimé.`);
            setConfirmDelete(null);
            await load();
        } catch (e) {
            // Verbatim French sentence from the backend — give the admin
            // 8 s to read the blocking reason.
            toastError(e.message, { duration: 8000 });
            setConfirmDelete(null);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="call-centers-page" className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-[#1A1D2E]">Call Centers</h2>
                    <p className="text-sm text-[#6B7280]">Gestion des centres d'appel et de la file de distribution</p>
                </div>
                <button
                    type="button"
                    data-testid="cc-create-button"
                    onClick={openCreate}
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-gradient-to-r from-[#4B3FD8] to-[#9B7FF4] text-white text-sm font-semibold hover:opacity-95"
                >
                    <Plus size={16} strokeWidth={2} />
                    Nouveau Call Center
                </button>
            </div>

            <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,0.05)] overflow-hidden">
                <div className="overflow-x-auto">
                    <table data-testid="cc-table" className="w-full min-w-[820px] text-sm">
                        <thead>
                            <tr className="bg-[#F5F6FA] text-[11px] uppercase tracking-wider text-[#6B7280]">
                                <Th>Nom</Th>
                                <Th>Nom commercial</Th>
                                <Th>Statut</Th>
                                <Th>Position dans la file</Th>
                                <Th>Couvert par</Th>
                                <Th className="text-right">Actions</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <TableRowSkeleton rows={4} columns={6} testId="cc-skeleton" />
                            )}
                            {!loading && error && (
                                <tr><td colSpan={6} className="text-center py-8 text-[#EF4444]" data-testid="cc-error">{error}</td></tr>
                            )}
                            {!loading && !error && items.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="py-2">
                                        <EmptyState
                                            testId="cc-empty"
                                            Icon={Headphones}
                                            title="Aucun call center configuré"
                                            description="Créez un premier call center pour commencer à distribuer les commandes."
                                            action={{ label: 'Nouveau Call Center', Icon: Plus, onClick: openCreate, testId: 'cc-empty-create' }}
                                        />
                                    </td>
                                </tr>
                            )}
                            {!loading && !error && items.map((cc, idx) => {
                                const isEditing = editingId === cc.id;
                                return (
                                    <tr
                                        key={cc.id}
                                        data-testid="cc-row"
                                        data-cc-id={cc.id}
                                        data-cc-name={cc.name}
                                        className={idx % 2 === 0 ? 'bg-white' : 'bg-[#F5F6FA]'}
                                    >
                                        <Td className="font-semibold text-[#1A1D2E]" data-testid="cc-row-name">
                                            <div className="inline-flex items-center gap-2">
                                                <span>{cc.name}</span>
                                                {cc.is_active && cc.unresolved_count > (cc.unresolved_threshold || 20) && (
                                                    <span
                                                        data-testid="cc-row-warning-badge"
                                                        title={`${cc.unresolved_count} commandes non résolues`}
                                                        className="inline-flex items-center gap-1 h-5 px-1.5 rounded-md bg-[#EF4444]/15 text-[#EF4444] text-[11px] font-semibold"
                                                    >
                                                        <AlertTriangle size={12} strokeWidth={2} />
                                                        {cc.unresolved_count}
                                                    </span>
                                                )}
                                            </div>
                                        </Td>
                                        <Td data-testid="cc-row-commercial">
                                            {isEditing ? (
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        data-testid="cc-commercial-input"
                                                        autoFocus
                                                        value={editingValue}
                                                        onChange={(e) => setEditingValue(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') saveEditCommercial(cc);
                                                            else if (e.key === 'Escape') cancelEditCommercial();
                                                        }}
                                                        className="h-8 px-2 rounded border border-[#E5E7EB] text-sm text-[#1A1D2E] w-[180px] focus:outline-none focus:ring-2 focus:ring-[#4B3FD8]/25"
                                                        placeholder="—"
                                                    />
                                                    <button data-testid="cc-commercial-save" onClick={() => saveEditCommercial(cc)} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-[#10B981]/10 text-[#10B981]"><Check size={16} /></button>
                                                    <button data-testid="cc-commercial-cancel" onClick={cancelEditCommercial} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-[#EF4444]/10 text-[#EF4444]"><X size={16} /></button>
                                                </div>
                                            ) : (
                                                <span className="text-[#1A1D2E]">{cc.commercial_name || <span className="text-[#6B7280]">—</span>}</span>
                                            )}
                                        </Td>
                                        <Td>
                                            <div className="flex items-center gap-3">
                                                <span
                                                    data-testid="cc-row-status-pill"
                                                    className={
                                                        'inline-flex items-center h-6 px-2 rounded-md text-[11px] font-semibold ' +
                                                        (cc.is_active
                                                            ? 'bg-[#10B981]/15 text-[#10B981]'
                                                            : 'bg-[#EF4444]/15 text-[#EF4444]')
                                                    }
                                                >
                                                    {cc.is_active ? 'ACTIF' : 'NON ACTIF'}
                                                </span>
                                                <Switch
                                                    data-testid="cc-row-toggle"
                                                    checked={cc.is_active}
                                                    onCheckedChange={(v) => onToggle(cc, v)}
                                                    className="data-[state=checked]:bg-[#10B981] data-[state=unchecked]:bg-[#E5E7EB]"
                                                />
                                            </div>
                                        </Td>
                                        <Td data-testid="cc-row-position" className="tabular-nums">
                                            {cc.is_active
                                                ? <span className="font-semibold text-[#1A1D2E]">{cc.queue_position}</span>
                                                : <span className="text-[#6B7280]">—</span>}
                                        </Td>
                                        <Td data-testid="cc-row-coverage">
                                            <select
                                                data-testid="cc-row-coverage-select"
                                                data-cc-id={cc.id}
                                                disabled={savingCoverageId === cc.id}
                                                value={cc.coverage_user_id || ''}
                                                onChange={(e) => onCoverageChange(cc, e.target.value)}
                                                className="h-8 px-2 pr-7 rounded border border-[#E5E7EB] text-sm text-[#1A1D2E] bg-white focus:outline-none focus:ring-2 focus:ring-[#4B3FD8]/25 disabled:opacity-60"
                                            >
                                                <option value="">Aucun</option>
                                                {/* Phase C.1.1 — filtre : agent titulaire d'un AUTRE CC uniquement.
                                                    Exclut super_admin (sans CC), managers, et le titulaire du CC courant. */}
                                                {activeUsers
                                                    .filter((u) => u.role === 'cc_agent'
                                                        && u.call_center_id
                                                        && u.call_center_id !== cc.id)
                                                    .map((u) => (
                                                        <option key={u.id} value={u.id}>
                                                            {u.display_name || u.email}
                                                        </option>
                                                    ))}
                                            </select>
                                        </Td>
                                        <Td className="text-right">
                                            <div className="inline-flex items-center gap-1">
                                                {!isEditing && (
                                                    <button
                                                        data-testid="cc-row-edit"
                                                        onClick={() => startEditCommercial(cc)}
                                                        className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-[#F5F6FA] text-[#6B7280] hover:text-[#4B3FD8]"
                                                        aria-label="Modifier le nom commercial"
                                                    >
                                                        <Pencil size={16} strokeWidth={1.75} />
                                                    </button>
                                                )}
                                                <button
                                                    data-testid="cc-row-delete"
                                                    onClick={() => setConfirmDelete({ cc })}
                                                    className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-[#EF4444]/10 text-[#EF4444]"
                                                    aria-label="Supprimer le call center"
                                                >
                                                    <Trash2 size={16} strokeWidth={1.75} />
                                                </button>
                                            </div>
                                        </Td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Create modal */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="bg-white" data-testid="cc-create-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-[#1A1D2E]">Nouveau Call Center</DialogTitle>
                        <DialogDescription className="text-[#6B7280]">
                            Le call center est créé désactivé et rejoindra la file en fin de position lorsque vous l'activerez.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <label className="flex flex-col gap-1">
                            <span className="text-[12px] uppercase tracking-wider text-[#6B7280] font-semibold">Nom</span>
                            <input
                                data-testid="cc-create-name"
                                value={createName}
                                onChange={(e) => setCreateName(e.target.value)}
                                className="h-10 px-3 rounded-lg border border-[#E5E7EB] text-sm text-[#1A1D2E] focus:outline-none focus:ring-2 focus:ring-[#4B3FD8]/25"
                                placeholder="CC09"
                            />
                        </label>
                        <label className="flex flex-col gap-1">
                            <span className="text-[12px] uppercase tracking-wider text-[#6B7280] font-semibold">Nom commercial (optionnel)</span>
                            <input
                                data-testid="cc-create-commercial"
                                value={createCommercial}
                                onChange={(e) => setCreateCommercial(e.target.value)}
                                className="h-10 px-3 rounded-lg border border-[#E5E7EB] text-sm text-[#1A1D2E] focus:outline-none focus:ring-2 focus:ring-[#4B3FD8]/25"
                                placeholder="ex: SANAE"
                            />
                        </label>
                        {createError && (
                            <div data-testid="cc-create-error" className="text-sm text-[#EF4444]">{createError}</div>
                        )}
                    </div>
                    <DialogFooter>
                        <button
                            type="button"
                            onClick={() => setCreateOpen(false)}
                            className="h-10 px-4 rounded-lg border border-[#E5E7EB] bg-white text-[#1A1D2E] text-sm hover:bg-[#F5F6FA]"
                        >
                            Annuler
                        </button>
                        <button
                            type="button"
                            data-testid="cc-create-submit"
                            onClick={submitCreate}
                            disabled={creating}
                            className="h-10 px-4 rounded-lg bg-gradient-to-r from-[#4B3FD8] to-[#9B7FF4] text-white text-sm font-semibold disabled:opacity-40"
                        >
                            {creating ? 'Création…' : 'Créer'}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Deactivation confirmation (destructive red button) */}
            <AlertDialog
                open={!!confirmDeactivate}
                onOpenChange={(open) => { if (!open) setConfirmDeactivate(null); }}
            >
                <AlertDialogContent data-testid="cc-deactivate-dialog" className="bg-white">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-[#1A1D2E]">Désactiver ce call center ?</AlertDialogTitle>
                        <AlertDialogDescription className="text-[#6B7280]">
                            {confirmDeactivate ? (
                                <span data-testid="cc-deactivate-dialog-message">
                                    {confirmDeactivate.cc.name} a <span className="font-semibold text-[#1A1D2E]">{confirmDeactivate.unresolved_count}</span> commande(s) non résolue(s).
                                </span>
                            ) : null}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={busy} data-testid="cc-deactivate-cancel">Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="cc-deactivate-confirm"
                            disabled={busy}
                            onClick={confirmDeactivateNow}
                            className="bg-[#EF4444] text-white hover:bg-[#DC2626] focus:ring-[#EF4444]"
                        >
                            Désactiver
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Delete confirmation (destructive red button) */}
            <AlertDialog
                open={!!confirmDelete}
                onOpenChange={(open) => { if (!open) setConfirmDelete(null); }}
            >
                <AlertDialogContent data-testid="cc-delete-dialog" className="bg-white">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-[#1A1D2E]">Supprimer ce call center ?</AlertDialogTitle>
                        <AlertDialogDescription className="text-[#6B7280]">
                            {confirmDelete ? `Êtes-vous sûr de vouloir supprimer ${confirmDelete.cc.name} ? Cette action est irréversible.` : ''}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={busy} data-testid="cc-delete-cancel">Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="cc-delete-confirm"
                            disabled={busy}
                            onClick={confirmDeleteNow}
                            className="bg-[#EF4444] text-white hover:bg-[#DC2626] focus:ring-[#EF4444]"
                        >
                            Supprimer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function Th({ children, className = '' }) {
    return <th className={`text-left px-3 py-2 font-semibold ${className}`}>{children}</th>;
}
function Td({ children, className = '', ...rest }) {
    return <td className={`px-3 py-3 text-[#1A1D2E] align-middle ${className}`} {...rest}>{children}</td>;
}
